import React from 'react';
import { PersonDataContext } from '../../contexts/person-data-context';
import { PreferencesContext } from '../../contexts/preferences-context';
import {
  getStartAClaimContent,
  IEmployerContent,
} from '../../utils/remote-config-manager';
import { models } from '../../utils/cms-client';
import { supplemental, enums } from '../../utils';
import * as dm from '../../data-models';
import { FullScreenLoadingIndicator } from '../base/full-screen-loading-indicator';
import {
  IKenticoData,
  KenticoDataContext,
} from '../../contexts/kentico-data-context';
import CardSection from './card-section';
import Button from '../base/button';
import { useHistory, useLocation } from 'react-router-dom';
import { Form, FormElement } from '@progress/kendo-react-form';
import { useLlPlanPath, useVBCoverage } from '../../hooks/use-data';
import { ClaimsContainer, Header, ButtonGroupContainer } from './tw-components';
import {
  filterCasesLastYear,
  redirectIfDuplicateClaims,
} from '../../utils/claims';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { useLDClient, LDClient } from '@launchdarkly/react-sdk';

const INTAKE_TYPE = 'intake-type';

dayjs.extend(utc);
dayjs.extend(isSameOrAfter);

export type Props = {
  children: React.ReactNode;
  id?: string;
  className?: string;
};

const handleUseEffect = async (p: {
  setContent: React.Dispatch<
    React.SetStateAction<models.IntakeSelection | undefined>
  >;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  employerPreferences: dm.EmployerPreferences | undefined;
  config: IKenticoData[enums.KenticoDataKeys.config];
  setSteps: React.Dispatch<React.SetStateAction<models.IntakeSection[]>>;
  ERContent: IEmployerContent | undefined;
  hasAnyCoverage: () => boolean | undefined;
  companyCode?: string;
  ldClient: LDClient | undefined;
}) => {
  const pageContent = await getStartAClaimContent();
  const steps = pageContent.data.item.elements.sections?.linkedItems || [];
  p.setContent(pageContent.data.item);

  const excludedPlans = (
    (p.ERContent?.excluded_leave_plans || []) as any[]
  ).map((excludedPlan: any) => excludedPlan.codename);

  let filteredSteps = steps;
  if (excludedPlans.length > 0) {
    filteredSteps = steps.map((step) => {
      if (step.elements?.content_items?.linkedItems) {
        step.elements.content_items.linkedItems =
          step.elements.content_items.linkedItems.filter(
            (item) =>
              !excludedPlans.includes(
                item.elements.intake?.value[0]?.codename ?? '',
              ),
          );
      }
      return step;
    });
  }

  const hideADAByEmployerList =
    p.config['hide_ada_by_employer']?.split('|') || [];
  const hideADA = hideADAByEmployerList.includes(p.companyCode);
  const showADA =
    !hideADA && p.employerPreferences?.preferences?.ADA === 'Carrier';

  const hasCoverage = p.hasAnyCoverage();
  const employerEnabled = await supplemental.isEmployerEnabled(p.companyCode);
  const showSupplemental = hasCoverage && employerEnabled;
  const showLLRedirect =
    p.ldClient?.boolVariation('enable-ll-redirect', false) ?? false;

  if (!showADA && filteredSteps[0]?.elements?.content_items) {
    filteredSteps[0].elements.content_items.linkedItems =
      filteredSteps[0].elements.content_items.linkedItems.filter(
        (item) => item.system.codename !== 'start__ada_card',
      );
  }
  if (!showSupplemental && filteredSteps[0]?.elements?.content_items) {
    filteredSteps[0].elements.content_items.linkedItems =
      filteredSteps[0].elements.content_items.linkedItems.filter(
        (item) => item.system.codename !== 'supplementary_benefits',
      );
  }
  if (!showLLRedirect && filteredSteps[0]?.elements?.content_items) {
    filteredSteps[0].elements.content_items.linkedItems =
      filteredSteps[0].elements.content_items.linkedItems.filter(
        (item) => item.system.codename !== 'll_redirect_card',
      );
  }

  //Remove first page if not an ADA Carrier
  p.setSteps(
    showADA || showSupplemental || showLLRedirect
      ? filteredSteps
      : filteredSteps.slice(1, filteredSteps.length),
  );
  p.setLoading(false);
};

const StartAClaim: () => React.ReactElement = () => {
  const { personData } = React.useContext(PersonDataContext);
  const { employerPreferences, ERContent } =
    React.useContext(PreferencesContext);
  const { config } = React.useContext(KenticoDataContext);
  const [loading, setLoading] = React.useState(true);
  const [formState, setFormState] = React.useState<any>({});
  const [content, setContent] = React.useState<
    models.IntakeSelection | undefined
  >(undefined);
  const [step, setStep] = React.useState(0);
  const [steps, setSteps] = React.useState<models.IntakeSection[]>([]);
  const history = useHistory();
  const { data: vbCoverageData, isFetching: vbCovFetching } = useVBCoverage();
  const hasAnyCoverage = React.useCallback(
    () => vbCoverageData?.hasCoverage || false,
    [vbCoverageData],
  );
  const ldClient = useLDClient();

  const now = new Date();
  const today = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
  );

  const url = useLocation();
  const [llPlanId] = React.useState(() =>
    new URLSearchParams(url.search).get('id'),
  );
  const { data: llPlanPath } = useLlPlanPath(llPlanId);
  const intakeType =
    typeof llPlanPath === 'string' ? llPlanPath : llPlanPath?.intakeType;
  const preFill =
    typeof llPlanPath === 'object' && llPlanPath !== null
      ? llPlanPath?.preFill
      : undefined;

  const duplicateClaims = filterCasesLastYear(personData, today);

  React.useEffect(() => {
    if (!llPlanId || llPlanPath === undefined) {
      return;
    }

    const queryParams = new URLSearchParams(url.search);
    const hasId = queryParams.has('id');
    queryParams.delete('id');

    const search = queryParams.toString();
    const searchString = search ? `?${search}` : '';

    const shouldReplacePath =
      !!intakeType &&
      intakeType !== 'default' &&
      url.pathname.includes('start');

    const pathname = shouldReplacePath
      ? url.pathname.replace('start', 'form/' + intakeType)
      : url.pathname;

    if (hasId || pathname !== url.pathname) {
      history.replace({
        ...url,
        pathname,
        search: searchString,
        state: {
          ...(typeof url.state === 'object' ? url.state : {}),
          ...(llPlanId ? { leaveLogicPlanId: llPlanId } : {}),
          ...(preFill ? { defaults: preFill } : {}),
        },
      });
    }
  }, [history, intakeType, llPlanId, llPlanPath, preFill, url]);

  React.useEffect(() => {
    handleUseEffect({
      setContent,
      setLoading,
      employerPreferences,
      config,
      setSteps,
      ERContent,
      hasAnyCoverage,
      companyCode: personData?.person?.companyCode,
      ldClient,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personData, employerPreferences, vbCoverageData]);

  const lastStepIndex = steps.length - 1;
  const isLastStep = lastStepIndex === step;
  const isPreviousStepsValid =
    steps
      .slice(0, step)
      .findIndex((currentStep: any) => currentStep.isValid === false) === -1;

  const onStepSubmit = React.useCallback(
    (event: { isValid: boolean; values: Record<string, any> }) => {
      const { isValid, values } = event;
      const leaveLogicPath = `${enums.AppPaths.externalRedirect}/leave-logic`;

      let nextPath: string | null = null;

      if (isValid && values[INTAKE_TYPE] === 'supplemental') {
        nextPath = enums.AppPaths.supplemental;
      } else if (values[INTAKE_TYPE] === 'leave-logic-redirect') {
        nextPath = leaveLogicPath;
      } else if (!isLastStep && isValid && values[INTAKE_TYPE] !== 'none') {
        nextPath = `${enums.AppPaths.form}/${values[INTAKE_TYPE]}`;
      } else if (isLastStep && isValid && isPreviousStepsValid) {
        nextPath = `${enums.AppPaths.form}/${values['leave-type']}`;
      }

      if (nextPath) {
        const redirectParams = {
          duplicateClaims,
          values,
          isValid,
          isLastStep,
          isPreviousStepsValid,
          history,
          duplicateClaimPathname: enums.AppPaths.duplicateClaim,
        } as const;

        if (nextPath === leaveLogicPath) {
          history.push({
            pathname: leaveLogicPath,
            state: { redirectUrl: config['leavelogic_url'] },
          });
        } else {
          const hasDuplicates = redirectIfDuplicateClaims(redirectParams);

          if (!hasDuplicates) {
            history.push(nextPath);
          }
        }

        return;
      }

      // Only update form step state if we are NOT navigating away
      if (isValid) {
        const currentSteps = steps.map((currentStep: any, index: number) => ({
          ...currentStep,
          isValid: index === step ? isValid : currentStep.isValid,
        }));

        setSteps(currentSteps);
        setStep(() => Math.min(step + 1, lastStepIndex));
        setFormState(values);
      }
    },
    [
      steps,
      isLastStep,
      isPreviousStepsValid,
      step,
      lastStepIndex,
      history,
      duplicateClaims,
      config,
    ],
  );

  const onPrevClick = React.useCallback(
    (event: React.SyntheticEvent) => {
      event.preventDefault();
      setStep(() => Math.max(step - 1, 0));
    },
    [step, setStep],
  );

  if (loading || vbCovFetching || (!!llPlanId && llPlanPath === undefined)) {
    return <FullScreenLoadingIndicator />;
  }

  return (
    <ClaimsContainer id="start-a-claim-container">
      <Header>{steps[step]?.elements?.header?.value}</Header>
      <Form
        initialValues={formState}
        onSubmitClick={onStepSubmit}
        render={(formRenderProps) => (
          <FormElement>
            <CardSection
              formRenderProps={formRenderProps}
              content={steps[step]}
              name={steps[step]?.elements?.name?.value ?? `step-${step}`}
            />
            <ButtonGroupContainer>
              {step !== 0 && (
                <Button
                  id="intake-back-button"
                  buttonStyle={enums.ButtonTypes.secondary}
                  style={{ marginRight: 'auto' }}
                  onClick={onPrevClick}
                >
                  {content?.elements?.back_button_text?.value}
                </Button>
              )}
            </ButtonGroupContainer>
          </FormElement>
        )}
      />
    </ClaimsContainer>
  );
};

export default StartAClaim;
